import { useState, useEffect, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, FormControl, Switch } from '@mui/material';
import ActionTypes from '../base/ActionTypes';
import { useSelector } from '../context/Store/StoreHooks';
import { IAction } from '../interfaces/IAction';
import { StyledFormLabel } from './BaseStyles';

const ControlRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  alignContent: 'center',
  justifyContent: 'flex-start',
  minHeight: '40px',
  padding: '5px 10px 10px 6px'
});

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'cursor',
})<{ cursor: boolean }>(({ cursor }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  ...(cursor ? { cursor: 'pointer' } : {
    cursor: 'not-allowed',
    color: '#CCCCCC',
  }),
}));

interface ICheckBoxFieldProps {
  Tran: string;
  PropertyName: string;
  Value: boolean;
  IsDisabled: boolean;
  ReadOnly: boolean;
  FieldName: string;
  Refresh: number;
  Redraw: number;
  ComponentMode: number;
  UpdatePropertyCollectionDictionary: (dictionary: any) => void;
  sendActionToParent: (event: IAction) => void;
  extraRightMargin?: string;
  mapStateToProps?: any;
  IsHidden?: boolean;
  Tags?: string[];
}

export function CheckBoxField({
  Tran,
  PropertyName,
  Value,
  IsDisabled,
  ReadOnly,
  FieldName,
  Refresh,
  Redraw,
  ComponentMode,
  UpdatePropertyCollectionDictionary,
  sendActionToParent,
  extraRightMargin,
  mapStateToProps,
  IsHidden,
  Tags,
}: ICheckBoxFieldProps) {

  const [tags] = useState<string[]>(Tags || []);
  const isDialogBlocked = useSelector((state) => state.dialogState.isDialogBlocked);
  const doesDialogHaveError = useSelector((state) => state.dialogState.doesDialogHaveError);
  const isPropertyGridBlocked = useSelector((state) => state.propertyGridState.isPropertyGridBlocked);
  const isDialogRefreshAllowed = useSelector((state) => state.dialogState.isDialogRefreshAlloed);

  const [checkboxWorkflowIndex, setCheckboxWorkflowIndex] = useState(0);
  const [isHidden, setIsHidden] = useState(IsHidden !== undefined ? IsHidden : false);

  // Component state
  const [isChecked, setIsChecked] = useState(Value || false);

  const componentMode = useMemo(() => ComponentMode || 0, [ComponentMode]);


  const disabled = useMemo(() => IsDisabled || ReadOnly || isDialogBlocked || isPropertyGridBlocked, [IsDisabled, ReadOnly, isDialogBlocked, isPropertyGridBlocked]);

  const updatePropertyDictionary = useCallback((newValue) => {
    const controlObject = {
      PropertyName: PropertyName,
      PropertyValue: newValue,
      RefreshType: Refresh,
      RedrawType: Redraw,
    };
    if (UpdatePropertyCollectionDictionary) {
      UpdatePropertyCollectionDictionary({ [PropertyName]: controlObject });
    }
  }, [UpdatePropertyCollectionDictionary, PropertyName, Refresh, Redraw]);


  const handleSwitchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (ReadOnly) return;
    setIsChecked(event.target.checked);
    setCheckboxWorkflowIndex(1);
  }, [ReadOnly]);

  useEffect(() => {
    if (checkboxWorkflowIndex === 1) {
      setCheckboxWorkflowIndex(2);
      if (componentMode === 0) {
        updatePropertyDictionary(isChecked);
      }
    }
  }, [ReadOnly, isChecked, componentMode, disabled, isPropertyGridBlocked, sendActionToParent, FieldName, PropertyName, Refresh, Redraw, updatePropertyDictionary, isDialogBlocked, doesDialogHaveError, isDialogRefreshAllowed, checkboxWorkflowIndex]);


  useEffect(() => {
    if (checkboxWorkflowIndex === 2) {
      setCheckboxWorkflowIndex(0);
      if (componentMode === 0) {
        updatePropertyDictionary(isChecked);
        if (disabled || isDialogBlocked || (doesDialogHaveError && !isDialogRefreshAllowed)) {
          return;
        }
      } else if (disabled || isPropertyGridBlocked) {
        return;
      }

      sendActionToParent({
        type: ActionTypes.UP_CheckboxStatusChangedAction,
        payload: {
          FieldName: FieldName,
          PropertyName: PropertyName,
          PropertyValue: isChecked,
        },
      });

      if (Refresh === 1 || Redraw === 1 || componentMode === 1) {
        sendActionToParent({
          type: ActionTypes.UP_RefreshPaneAction,
          payload: {
            FieldName: FieldName,
            PropertyName: PropertyName,
            PropertyValue: isChecked,
            RefreshType: Refresh,
            RedrawType: Redraw,
          },
        });
      }
    }
  }, [ReadOnly, isChecked, componentMode, disabled, isPropertyGridBlocked, sendActionToParent, FieldName, PropertyName, Refresh, Redraw, updatePropertyDictionary, isDialogBlocked, doesDialogHaveError, isDialogRefreshAllowed, checkboxWorkflowIndex]);

  useEffect(() => {
    if (mapStateToProps) {
      const unsubscribeMapStateToProps = mapStateToProps.subscribe(({ type, payload }: IAction) => {
        switch (type) {
          case ActionTypes.DOWN_ShowHideChildControl:
            {
              let applyChange = true;
              payload.tags.forEach((item) => {
                if (tags && tags.some(i => i === item)) {
                  //Do nothing
                } else {
                  applyChange = false;
                }
              });
              if (applyChange) {
                setIsHidden(!payload.visibleState);
              }
            }

            break;
        }
      });

      return () => {
        if (unsubscribeMapStateToProps) {
          unsubscribeMapStateToProps();
        }
      };
    }
  }, [mapStateToProps, tags]);


  useEffect(() => {
    updatePropertyDictionary(Value);
    setIsChecked(Value);
  }, [Value, updatePropertyDictionary]);


  return (
    !isHidden ?
      <div style={{ marginRight: extraRightMargin }}>
        <ControlRow>
          <StyledFormLabel>
            <StyledFormControl cursor={!ReadOnly}>
              <Switch
                id={`switch_${PropertyName}`}
                checked={isChecked}
                onChange={handleSwitchChange}
                disabled={disabled}
                color="primary"
                slotProps={{
                  input: {
                    'aria-label': Tran,
                    'data-testid': isChecked ? `switch_${PropertyName}_selected` : `switch_${PropertyName}_not_selected`,
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
              <Box sx={{ fontWeight: 'normal'}} className={'vjcontrol'} >{Tran}</Box>
            </StyledFormControl>
          </StyledFormLabel>
        </ControlRow>
      </div> : null
  );
}
